import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Trophy, Gift, Inbox, Undo2, AlertTriangle, CheckCircle2,
} from "lucide-react";

/**
 * Who finished the month, and who has not had the coupon lately.
 *
 * The tutor's actual problem, in their words (1 Nov 2026): more than one
 * child will hit the target, so picking a winner is not the hard part -
 * *"I want to not to give coupon to the same child again and again"*. So
 * this screen does not choose. It lists everyone with the figures, puts the
 * children who have never had a coupon at the top of equal scores, and shows
 * beside every name the month they last got one. The decision stays a
 * person's; the remembering is the part a person is bad at.
 *
 * **The suggested order is not a verdict.** Most finished first, then fewest
 * coupons, then longest since the last one. A tutor who wants to give it to
 * the most improved rather than the highest can, and nothing here argues.
 *
 * **Nothing on this screen is visible to a child.** Their own portal shows
 * their bar and their rung and no one else's name. A named ranking of other
 * people's children is personal data about minors and does not belong in a
 * place a parent might screenshot.
 */

const TONE = {
  Bronze: "bg-amber-50 text-amber-800 border-amber-200",
  Silver: "bg-slate-50 text-slate-700 border-slate-300",
  Gold: "bg-yellow-50 text-yellow-800 border-yellow-300",
  Platinum: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const BAR = {
  Bronze: "bg-amber-600", Silver: "bg-slate-400",
  Gold: "bg-yellow-500", Platinum: "bg-emerald-600",
};

function monthName(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function StarOfTheMonth() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [busy, setBusy] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(() => {
    setState("loading");
    Promise.all([
      api.get(`/admin/badges?month=${month}`),
      api.get("/admin/coupons"),
    ])
      .then(([b, c]) => {
        setData(b.data);
        setCoupons(c.data?.coupons || []);
        setState("ok");
      })
      .catch(() => setState("failed"));
  }, [month]);

  useEffect(load, [load]);

  async function give(student) {
    setBusy(student.id);
    try {
      await api.post("/admin/coupons", { student_id: student.id, month });
      toast.success(`${student.name} has ${monthName(month)}'s coupon`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record that");
    } finally {
      setBusy(null);
    }
  }

  async function undo(coupon) {
    setBusy(coupon.id);
    try {
      await api.delete(`/admin/coupons/${coupon.id}`);
      toast.success("Taken back");
      load();
    } catch {
      toast.error("Could not take it back");
    } finally {
      setBusy(null);
    }
  }

  const rows = data?.students || [];
  const given = coupons.filter((c) => c.month === month);

  return (
    <AdminShell title="Star of the month"
      subtitle="Who finished the month, and who is due a turn.">

      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm text-slate-600" htmlFor="month">Month</label>
        <input id="month" type="month" value={month} data-testid="month-picker"
          onChange={(e) => setMonth(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
      </div>

      {state === "loading" && (
        <div className="bg-white rounded-xl border border-slate-200 py-16 flex justify-center">
          <Loader2 size={22} className="animate-spin text-slate-400" />
        </div>
      )}

      {state === "failed" && (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-red-700 flex gap-3">
          <AlertTriangle size={18} /> Could not load this month.
        </div>
      )}

      {state === "ok" && given.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold mb-2">
            <Gift size={16} /> {monthName(month)} already awarded
          </div>
          <div className="flex flex-wrap gap-2">
            {given.map((c) => (
              <span key={c.id}
                className="inline-flex items-center gap-2 bg-white border border-emerald-200
                           rounded-full pl-3 pr-1.5 py-1 text-sm">
                {c.student_name}
                <button onClick={() => undo(c)} disabled={busy === c.id}
                  title="Take it back" data-testid={`undo-${c.id}`}
                  className="text-slate-400 hover:text-red-600 p-1 rounded-full">
                  <Undo2 size={13} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {state === "ok" && rows.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500">
          <Inbox size={24} className="mx-auto mb-3 text-slate-300" />
          No students yet.
        </div>
      )}

      {state === "ok" && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Student</th>
                <th className="px-4 py-3 font-medium">This month</th>
                <th className="px-4 py-3 font-medium">Stage</th>
                <th className="px-4 py-3 font-medium">Self-marking</th>
                <th className="px-4 py-3 font-medium">Coupons</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const s = r.summary || {};
                const c = r.coupons || {};
                const sm = r.self_marking || {};
                return (
                  <tr key={r.student.id} data-testid={`row-${r.student.id}`}
                      className={c.this_month ? "bg-emerald-50/40" : ""}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{r.student.name}</div>
                      <div className="text-xs text-slate-500">
                        {[r.student.year && `Year ${r.student.year}`, r.student.tier]
                          .filter(Boolean).join(" · ")}
                      </div>
                    </td>

                    <td className="px-4 py-3 w-56">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1">
                        <div className={`h-full rounded-full ${BAR[s.stage] || "bg-sky-500"}`}
                             style={{ width: `${Math.min(100, s.percent || 0)}%` }} />
                      </div>
                      <div className="text-xs text-slate-500">
                        {s.done} of {s.target}
                        {s.percent != null && <> · {s.percent}%</>}
                      </div>
                      {/* A perfect three days after joining on the 28th is a
                          real 100% and the child is right to see it as one.
                          It is still not the same month as thirty, and the
                          tutor is the one who needs to know that. */}
                      {s.part_month && (
                        <div className="text-xs text-sky-700 mt-0.5">
                          part month · {s.month_days} days ran
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {s.stage ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold
                                          px-2.5 py-1 rounded-full border ${TONE[s.stage]}`}>
                          <Trophy size={12} />{s.stage}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>

                    {/* Where the child and the tutor both marked the same
                        question, how often they agreed. A signed gap, because
                        marking yourself up and marking yourself down are two
                        different conversations. */}
                    <td className="px-4 py-3">
                      {sm.compared ? (
                        <div>
                          <span className={sm.percent >= 80 ? "text-green-700" : "text-amber-700"}>
                            {sm.percent}% agreed
                          </span>
                          <div className="text-xs text-slate-500">
                            {sm.compared} checked
                            {sm.gap !== 0 && <> · {sm.gap > 0 ? "+" : ""}{sm.gap} marks</>}
                          </div>
                        </div>
                      ) : <span className="text-slate-400 text-xs">nothing to compare</span>}
                    </td>

                    <td className="px-4 py-3">
                      {c.total ? (
                        <div>
                          <div className="text-slate-700">{c.total}</div>
                          <div className="text-xs text-slate-500">last {monthName(c.last_month)}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-sky-700 font-medium">never had one</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {c.this_month ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 size={14} /> awarded
                        </span>
                      ) : (
                        <button onClick={() => give(r.student)} disabled={busy === r.student.id}
                          data-testid={`give-${r.student.id}`}
                          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg
                                     border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
                          <Gift size={14} /> Give coupon
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The rotation, in one list. The question this answers is "who have I
          been quietly skipping", which no per-month view can. */}
      {state === "ok" && coupons.length > 0 && (
        <div className="mt-8">
          <h3 className="font-serif text-lg brand-navy mb-3">Every coupon so far</h3>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {coupons.map((c) => (
              <div key={c.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <span className="text-slate-700">{c.student_name}</span>
                <span className="text-slate-500">{monthName(c.month)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
